/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Logger } from '@saviynt/common';

import type { GenericAttributeMapping } from '../../../components/AttributeMapping';
import type { RootState } from '../../../services/serviceTypes';
import {
  clearEntitlementMessages,
  setFormStatus,
  setSavedStepData,
  updateEntitlements,
  updateStepFormData,
} from '../data-store/copilotSlice';
import { buildCumulativePayload } from '../utils/mappingReplacementUtils';
import { processPayloadByMode } from '../utils/payloadProcessingUtils';

interface SystemNameField {
  value?: string;
}

interface StepData {
  mappings?: GenericAttributeMapping[];
  entitlementIndex?: number;
}

type OnFormSubmitFunction = (
  stepName: string,
  payload: Record<string, unknown>,
  isValid: boolean
) => void;

const useAddAccessAttributeMappingLogic = () => {
  const dispatch = useDispatch();

  const [allMappings, setAllMappings] = useState<
    Record<number, GenericAttributeMapping[]>
  >({});

  const [allSavedStates, setAllSavedStates] = useState<Record<number, boolean>>(
    {}
  );
  const [allValidationErrors, setAllValidationErrors] = useState<
    Record<number, string | null>
  >({});

  const entitlements = useSelector(
    (state: RootState) => state.copilot.entitlementIteration.entitlements
  );

  const transformedTemplates = useSelector(
    (state: RootState) => state.copilot.addAccessEntitlementTemplates
  );

  const isEditMode = useSelector(
    (state: RootState) => state.copilot.ui.mode === 'edit'
  );

  const applicationName = useSelector((state: RootState) => {
    const generalFormData = state.copilot.stepFormData.General ?? {};
    const systemNameField = generalFormData['securitysystems.systemname'] as
      | SystemNameField
      | string
      | undefined;
    const name =
      (typeof systemNameField === 'object' && systemNameField.value) ||
      (typeof systemNameField === 'string' ? systemNameField : '') ||
      '';

    return String(name);
  });

  const stepFormData = useSelector(
    (state: RootState) => state.copilot.stepFormData
  );

  const addAccessPrompts = useSelector(
    (state: RootState) => state.copilot.addAccessPrompts || {}
  );

  const addAccessTemplateData = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    (state: RootState) => state.copilot.parsedTemplates.AddAccess || {}
  );

  // Populate entitlements from addAccessTemplateData in edit mode
  useEffect(() => {
    if (
      isEditMode &&
      entitlements.length === 0 &&
      addAccessTemplateData?.attributeValue
    ) {
      try {
        const attributeValue = JSON.parse(
          addAccessTemplateData.attributeValue as string
        ) as { call?: { name?: string }[] };

        if (Array.isArray(attributeValue.call)) {
          const templateEntitlements = attributeValue.call.map(
            (attr, index) => ({
              id: index,
              value: String(attr.name || `Entitlement ${index + 1}`),
            })
          );

          dispatch(updateEntitlements(templateEntitlements));
        }
      } catch (error) {
        Logger.error('Error parsing AddAccess attributeValue:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    entitlements.length,
    addAccessTemplateData?.attributeValue,
    dispatch,
  ]);

  // Initialize mappings for current entitlement in edit mode
  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    entitlements.forEach((_, index) => {
      const existingStepData = stepFormData[`AddAccess_${index}`] as
        | StepData
        | undefined;
      const existingMappings = existingStepData?.mappings ?? [];

      // Update if mappings exist in stepFormData but not in allMappings
      if (
        existingMappings.length > 0 &&
        (!allMappings[index] || allMappings[index].length === 0)
      ) {
        setAllMappings((prev) => ({
          ...prev,
          [index]: existingMappings,
        }));
      }
    });
  }, [entitlements, stepFormData, allMappings, isEditMode]);

  const mandatoryMappings: GenericAttributeMapping[] = useMemo(
    () => [
      {
        id: 'mandatory-1',
        saviyntField: '',
        externalAppField: '',
        saviyntValue: '',
        externalAppValue: '',
        isMandatory: true,
      },
      {
        id: 'mandatory-2',
        saviyntField: '',
        externalAppField: '',
        saviyntValue: '',
        externalAppValue: '',
        isMandatory: true,
      },
    ],
    []
  );

  const handleMappingsChange = useCallback(
    (entitlementIndex: number, newMappings: GenericAttributeMapping[]) => {
      setAllMappings((prev) => ({
        ...prev,
        [entitlementIndex]: newMappings,
      }));

      // Store in Redux immediately on change (like reference)
      const validMappings = newMappings.filter(
        (mapping) => mapping.saviyntField && mapping.externalAppField
      );

      if (validMappings.length > 0) {
        const mappingObject = validMappings.reduce<Record<string, string>>(
          (acc, mapping) => {
            if (mapping.saviyntField && mapping.externalAppField) {
              acc[String(mapping.externalAppField)] = `\${${String(
                mapping.saviyntField
              )}}`;
            }

            return acc;
          },
          {}
        );

        const mappingJson = JSON.stringify(mappingObject)
          .slice(1, -1)
          .replace(/"/g, '\\"');

        const jsonKey = `externalconnattvalue.AddAccessJSON`;
        const payload = {
          multipleconnection: false,
          [jsonKey]: `{"${mappingJson}"}`,
          mappings: validMappings.map((mapping) => ({
            id: mapping.id,
            saviyntField: mapping.saviyntField,
            externalAppField: mapping.externalAppField,
            saviyntValue: mapping.saviyntValue,
            externalAppValue: mapping.externalAppValue,
            isMandatory: mapping.isMandatory,
          })),
          entitlementIndex,
        };

        dispatch(
          updateStepFormData({
            stepName: `AddAccess_${entitlementIndex}`,
            formData: payload,
          })
        );

        // Also populate RemoveAccess stepFormData with same mappings
        dispatch(
          updateStepFormData({
            stepName: `RemoveAccess_${entitlementIndex}`,
            formData: {
              mappings: validMappings.map((mapping) => ({
                id: mapping.id,
                saviyntField: mapping.saviyntField,
                externalAppField: mapping.externalAppField,
                saviyntValue: mapping.saviyntValue,
                externalAppValue: mapping.externalAppValue,
                isMandatory: mapping.isMandatory,
              })),
              entitlementIndex,
            },
          })
        );

        // Store template data in step form data
        dispatch(
          updateStepFormData({
            stepName: `AddAccessTemplate_${entitlementIndex}`,
            formData: { template: payload },
          })
        );
      }
    },
    [dispatch]
  );

  const validateAndProcessNext = useCallback(
    (
      entitlementIndex: number,
      template: string,
      onFormSubmit: OnFormSubmitFunction
    ) => {
      const currentMappings = allMappings[entitlementIndex];
      const validMappings = currentMappings.filter(
        (mapping) => mapping.saviyntField && mapping.externalAppField
      );

      if (validMappings.length < 2) {
        setAllValidationErrors((prev) => ({
          ...prev,
          [entitlementIndex]:
            'At least two attribute mapping is required to add access.',
        }));

        return false;
      }

      // Collapse the current form after successful validation
      dispatch(
        setFormStatus({
          stepName: `AddAccess_${entitlementIndex}`,
          formStatus: 'collapsedview',
        })
      );

      // Clear messages after current entitlement
      dispatch(
        clearEntitlementMessages({
          entitlementIndex,
          entitlementType: 'addAccess',
          keepForm: true,
        })
      );

      setAllValidationErrors((prev) => ({
        ...prev,
        [entitlementIndex]: null,
      }));

      // Create payload matching stepFormData structure
      const savedMappingObject = currentMappings.reduce<Record<string, string>>(
        (acc, mapping) => {
          if (mapping.saviyntField && mapping.externalAppField) {
            acc[String(mapping.externalAppField)] = `\${${String(
              mapping.saviyntField
            )}}`;
          }

          return acc;
        },
        {}
      );

      const savedMappingJson = JSON.stringify(savedMappingObject)
        .slice(1, -1)
        .replace(/"/g, '\\"');

      const jsonKey = `externalconnattvalue.AddAccessJSON`;
      const stepFormPayload = {
        multipleconnection: false,
        [jsonKey]: `{"${savedMappingJson}"}`,
        mappings: currentMappings,
        entitlementIndex,
      };

      // Edit mode: Use utility functions and Redux state
      if (isEditMode) {
        const confirmationPayload = stepFormData.AddAccessConfirmation;

        if (confirmationPayload) {
          // Use utility function to process payload with all entitlements and prompts
          const processedPayload = processPayloadByMode(true, {
            payload: confirmationPayload,
            currentIndex: entitlementIndex,
            allPrompts: addAccessPrompts,
            currentPrompt: null,
            totalEntitlements: entitlements.length,
          });

          dispatch(
            setSavedStepData({
              stepName: `AddAccess_${entitlementIndex}`,
              formData: stepFormPayload,
            })
          );

          onFormSubmit('AddAccessConfirmation', processedPayload, true);

          return true;
        }
      }

      // Create mode: Build cumulative payload up to current entitlement
      const payload = buildCumulativePayload(
        template,
        entitlements,
        stepFormData,
        entitlementIndex,
        addAccessPrompts
      );

      dispatch(
        setSavedStepData({
          stepName: `AddAccess_${entitlementIndex}`,
          formData: stepFormPayload,
        })
      );
      onFormSubmit('AddAccessConfirmation', payload, true);

      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      allMappings,
      entitlements,
      transformedTemplates,
      dispatch,
      isEditMode,
      addAccessPrompts,
    ]
  );

  const createDynamicConfig = useCallback(
    (
      attributesData:
        | { tableArributes?: Record<string, { name: string }[]> }
        | undefined
    ) => {
      if (!attributesData?.tableArributes) {
        return {
          saviyntApp: {
            name: 'Saviynt',
            icon: 'SaviyntLogoFilledSeparateColors',
            fields: [],
            allowCustomValues: false,
          },
          externalApp: {
            name: applicationName,
            icon: applicationName,
            fields: [],
            allowCustomValues: true,
          },
        };
      }

      const flattenedAttributes = Object.entries(attributesData.tableArributes)
        .flatMap(([parentKey, attributes]): string[] => {
          if (Array.isArray(attributes)) {
            return attributes.map(
              (attr: { name: string }) => `${parentKey}.${attr.name}`
            );
          }

          return [];
        })
        .concat(['password.'])
        .sort((a, b) => {
          const priorityOrder = [
            'usersObj',
            'arstasksObj',
            'usersManagerObj',
            'endpointsObj',
            'entitlementValuesObj',
            'password',
          ];

          const getPriority = (str: string) => {
            const index = priorityOrder.findIndex((prefix) =>
              str.startsWith(prefix)
            );

            return index === -1 ? priorityOrder.length : index;
          };

          const priorityA = getPriority(a);
          const priorityB = getPriority(b);

          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }

          const getAttributeLength = (str: string) => {
            const parts = str.split('.');

            return parts.length > 1 ? parts[1].length : str.length;
          };

          return getAttributeLength(a) - getAttributeLength(b);
        });

      return {
        saviyntApp: {
          name: 'Saviynt',
          icon: 'SaviyntLogoFilledSeparateColors',
          fields: flattenedAttributes,
          allowCustomValues: false,
        },
        externalApp: {
          name: applicationName,
          icon: applicationName,
          fields: [],
          allowCustomValues: true,
        },
      };
    },
    [applicationName]
  );

  const handleNext = useCallback(
    (
      entitlementIndex: number,
      template: string,
      onFormSubmit?: OnFormSubmitFunction
    ) => {
      setAllSavedStates((prev) => ({ ...prev, [entitlementIndex]: true }));

      return validateAndProcessNext(
        entitlementIndex,
        template,
        onFormSubmit || (() => {})
      );
    },
    [validateAndProcessNext]
  );

  return {
    allMappings,
    entitlements,
    allValidationErrors,
    allSavedStates,
    applicationName,
    mandatoryMappings,
    handleMappingsChange,
    validateAndProcessNext,
    handleNext,
    createDynamicConfig,
  };
};

export default useAddAccessAttributeMappingLogic;
